/**
 * clarity.ts — tag Microsoft Clarity sessions so a specific client's session can
 * actually be FOUND.
 *
 * WHY THIS EXISTS (John, 2026-08-29). Clarity has been recording since
 * 2026-07-26 (project w96pruttxy) and every session is ANONYMOUS — there was no
 * `identify` call anywhere. So the recordings existed and could not be reached.
 *
 * The case that proved it: Todd reported a 100%-off comp code that showed through
 * checkout and vanished at the payment screen, leaving him facing $6,000/yr. The
 * first diagnosis read the timestamps and concluded he entered it late; John
 * pushed back with Todd's own words and Todd was right. His session was almost
 * certainly recorded — 08-28, inside both the tag's live date and Clarity's ~30-day
 * retention — and finding it meant scrubbing weeks of unlabelled sessions. A
 * thirty-second answer was sitting there, unreachable.
 *
 * ⚠️ PRIVACY BOUNDARY — read before adding a field.
 * The Clarity install comment requires the project's masking to be "Mask all" so
 * replays never capture the CLIENT'S CUSTOMERS' PII (names, phones, invoice
 * amounts). Nothing here weakens that. What we send is OUR OWN identifiers for
 * OUR OWN paying client — an opaque internal clientId, and the business name so a
 * human can recognise the session in a list. We do NOT send an email, a phone
 * number, a customer name, or any money figure. Those belong to the client's
 * customers, and that is the data the masking exists to protect.
 */

type ClarityFn = (...args: unknown[]) => void;

function clarity(): ClarityFn | null {
  const w = window as unknown as { clarity?: ClarityFn };
  return typeof w.clarity === 'function' ? w.clarity : null;
}

/**
 * Attach the signed-in client to this session.
 *
 * Safe to call repeatedly and safe to call before the tag has loaded — the
 * snippet queues calls, and if it never loads this is a no-op rather than a
 * crash. Analytics tagging must never be able to break the page it is on.
 */
export function identifyClarityClient(input: {
  clientId?: string | null;
  businessName?: string | null;
  /** Set true for demo/preview sessions so they can be filtered OUT. */
  isDemo?: boolean;
}): void {
  try {
    const c = clarity();
    if (!c) return;
    const id = input.clientId?.trim();
    if (!id) return;

    c('identify', id);
    c('set', 'clientId', id);
    if (input.businessName?.trim()) c('set', 'businessName', input.businessName.trim());
    // ⚠️ Tag demo/preview explicitly. Without it, showcase walkthroughs sit in the
    // same list as real clients and every friction number is quietly inflated by
    // sessions that are not customers at all.
    c('set', 'accountType', input.isDemo ? 'demo' : 'client');
  } catch {
    /* never let analytics break the app */
  }
}

/**
 * Tag a NON-authenticated funnel session (checkout, marketing) where there is no
 * clientId yet.
 *
 * ⚠️ Deliberately takes no person-identifying argument. A prospect at checkout has
 * no client record, and the useful discriminator is not who they are but WHAT THEY
 * WERE DOING — the promo code they applied and the step they reached. That is what
 * would have located Todd's session, and it carries no PII.
 */
export function tagClarityFunnel(input: {
  stage?: string | null;
  promoCode?: string | null;
}): void {
  try {
    const c = clarity();
    if (!c) return;
    if (input.stage?.trim()) c('set', 'funnelStage', input.stage.trim());
    if (input.promoCode?.trim()) c('set', 'promoCode', input.promoCode.trim().toUpperCase());
  } catch {
    /* never let analytics break the page */
  }
}
